import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, Select, Tooltip } from 'antd';
import styled from 'styled-components';
import { useUpdateEffect } from 'usehooks-ts';
import { useProcContext } from '@/entities/proc';
import { themeConfig, useThemeContext } from '@/shared/theme';
import {
  ConditionComponentsType,
  LogicComponentOperator,
  LogicRule,
} from '@/shared/types';
import { Col, Row } from '@/shared/ui';
import { useLogicTabContext } from './ProcLogicTabProvider';

enum OperatorOptions {
  'is' = 'is',
  'is not' = 'isNot',
}

type Props = {
  rule: LogicRule;
  stepLogicsData: any;
  endStepId?: string;
};

export const ProcLogicRule: FC<Props> = ({
  rule,
  stepLogicsData,
  endStepId = 'procEnd',
}) => {
  const { theme } = useThemeContext();
  const { proc, selectedStep, changesSaved, setChangesSaved, resetLogicCount } =
    useProcContext();
  const { rules, setRules } = useLogicTabContext();

  const [selectedComponent, setSelectedComponent] = useState<any>(
    rule?.component?.id && {
      item: rule?.component,
      value: rule?.component?.id,
    },
  );
  const [operator, setOperator] = useState<LogicComponentOperator>(
    rule?.operator || OperatorOptions.is,
  );
  const [componentValuesOptions, setComponentValuesOptions] = useState<any>();
  const [componentValue, setComponentValue] = useState<string | undefined>(
    rule.value as string,
  );
  const [goToStep, setGoToStep] = useState<string>(rule.goToStep as string);

  const componentSelectOptions = useMemo(() => {
    const items: any = [];
    selectedStep?.components?.forEach((cmp: any) => {
      if (Object.values(ConditionComponentsType).includes(cmp.component)) {
        items.push({
          label: cmp.componentName,
          value: cmp.id,
          item: { ...cmp, type: cmp.component },
        });
      }
    });
    return items;
  }, [selectedStep]);

  const stepsSelectOptions = useMemo(() => {
    const items: any = [];
    proc?.stepData?.steps.forEach((step: any) => {
      if (step.id !== selectedStep?.id) {
        items.push({
          label: step.title,
          value: step.id,
        });
      }
    });
    items.push({
      label: 'Proc end',
      value: endStepId,
    });
    return items;
  }, [selectedStep?.id, proc?.stepData?.steps]);

  const getComponentValueOptions = useCallback(
    (component: ConditionComponentsType) => {
      switch (component) {
        case ConditionComponentsType.Checkbox:
          return [
            {
              label: 'true',
              value: true,
            },
            {
              label: 'false',
              value: false,
            },
          ];

        case ConditionComponentsType.Select: {
          const foundComponent = selectedStep?.components?.find(
            (cmp) => cmp.id === selectedComponent?.item?.id,
          );
          return foundComponent?.options || selectedComponent?.item?.options;
        }

        default:
          return [selectedStep, selectedComponent];
      }
    },
    [selectedStep?.id, selectedComponent],
  );

  const handleFormReset = () => {
    const ruleDataBeforeEdit = stepLogicsData?.find(
      (itemData: any) => itemData.id === rule.id,
    );

    if (ruleDataBeforeEdit) {
      const newRules = rules.map((item) => {
        if (item.id !== rule.id) return item;
        return {
          id: item.id,
          component: {
            id: ruleDataBeforeEdit.component.id,
            type: ruleDataBeforeEdit.component.type,
          },
          operator: ruleDataBeforeEdit.operator,
          value: ruleDataBeforeEdit.value,
          goToStep: ruleDataBeforeEdit.goToStep,
        };
      });
      setRules(newRules);
      setSelectedComponent({
        item: ruleDataBeforeEdit.component,
        value: ruleDataBeforeEdit.component.id,
      });
      setComponentValue(ruleDataBeforeEdit.value);
      setOperator(ruleDataBeforeEdit.operator);
      setGoToStep(ruleDataBeforeEdit.goToStep);
    } else {
      // rule was added and not saved
      const newRules = rules.filter((item) => item.id !== rule.id);
      setRules(newRules);
    }
  };

  const handleRuleRemove = () => {
    const newRules = rules.filter(
      (ruleItem: LogicRule) => rule.id !== ruleItem.id,
    );
    setRules(newRules);
    setChangesSaved(false);
  };

  useEffect(() => {
    if (selectedComponent) {
      setComponentValuesOptions(
        getComponentValueOptions(selectedComponent?.item?.type),
      );
    }
  }, [selectedComponent, selectedStep?.id]);

  useEffect(() => {
    if (
      changesSaved &&
      rule.value !== undefined &&
      componentValue === undefined
    ) {
      // initial render
      setComponentValue(rule.value as any);
    }

    if (!changesSaved) {
      setComponentValue(undefined);
    }
  }, [componentValuesOptions]);

  useUpdateEffect(() => {
    handleFormReset();
  }, [resetLogicCount]);

  useEffect(() => {
    setRules(
      rules.map((ruleItem) => {
        if (ruleItem.id !== rule.id) return ruleItem;
        return {
          id: ruleItem.id,
          component: {
            id: selectedComponent?.item?.id,
            type: selectedComponent?.item?.type,
          },
          operator,
          value: componentValue,
          goToStep,
        };
      }),
    );
  }, [selectedComponent, operator, componentValue, goToStep]);

  return (
    <Wrapper
      alignItems="flex-start"
      gap={15}
      style={{ background: themeConfig[theme].colors.contentBgSecondary }}
    >
      <div>If</div>
      <Select
        value={selectedComponent?.value}
        onChange={(value, option) => {
          setSelectedComponent({
            ...option,
            item: { ...option.item, type: option.item.component },
          });
          setChangesSaved(false);
        }}
        options={componentSelectOptions || []}
        style={{ width: '100%' }}
      />
      <Row>
        <Select
          value={operator}
          onChange={(value: LogicComponentOperator) => {
            setOperator(value);
            setChangesSaved(false);
          }}
          options={Object.entries(OperatorOptions).map((entry) => ({
            label: entry[0],
            value: entry[1],
          }))}
          style={{ minWidth: 100 }}
        />
        {componentValuesOptions?.length > 0 && (
          <Select
            value={componentValue}
            onChange={(value: LogicComponentOperator) => {
              setComponentValue(value);
              setChangesSaved(false);
            }}
            options={componentValuesOptions}
            style={{ width: '100%' }}
          />
        )}
      </Row>
      <div>Then go to</div>
      {stepsSelectOptions?.length > 0 && (
        <Select
          value={goToStep}
          onChange={(value) => {
            setGoToStep(value);
            setChangesSaved(false);
          }}
          options={stepsSelectOptions}
          style={{ width: '100%' }}
        />
      )}
      <Tooltip title="Delete rule" mouseEnterDelay={0.7}>
        <Button
          type="text"
          danger
          icon={<DeleteOutlined style={{ fontSize: 14 }} />}
          onClick={handleRuleRemove}
          style={{ marginLeft: 'auto' }}
        />
      </Tooltip>
    </Wrapper>
  );
};

const Wrapper = styled(Col)`
  padding: 15px;
  border-radius: 6px;
`;
